import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { axiosInstance } from "../../utils/axiosInstance";

// Asynchronous Thunks
export const fetchApiDetails = createAsyncThunk(
  "apiDetails/fetchData",
  async (params: {
    appId: number;
    pageNumber: number;
    pageSize: number;
    endpointPath: string[];
    apiName: string[];
    description: string[];
  }) => {
    const response = await axiosInstance.post(
      `/featsetup/api-details?appId=${params.appId}&page_no=${params.pageNumber}&page_size=${params.pageSize}`,
      {
        endpoint_path: params.endpointPath || [],
        api_name: params.apiName || [],
        description: params.description || [],
      },
    );
    return response.data;
  },
);

// Types and Interfaces
export type apiDetailsList = apiDetailsObject[];

export interface apiDetailsObject {
  id: number;
  total_count: number;
  apiName: string;
  endpointPath: string;
  description: string;
}

export type apiDetailsState = {
  data: apiDetailsList;
  totalListCount: number;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  editData: apiDetailsObject;
  saveData: apiDetailsObject;
  tableRefresh: number;
};

const initialState: apiDetailsState = {
  data: [],
  totalListCount: 0,
  status: "idle",
  error: null,
  tableRefresh: 0,
  editData: {
    id: 0,
    apiName: "",
    endpointPath: "",
    description: "",
    total_count: 0,
  },
  saveData: {
    id: 0,
    apiName: "",
    endpointPath: "",
    description: "",
    total_count: 0,
  },
};

// Slice
const apiDetailsSlice: any = createSlice({
  name: "apiDetails",
  initialState,
  reducers: {
    editApiData: (state, action: PayloadAction<apiDetailsObject>) => {
      state.editData = action.payload;
    },
    saveApiData: (state, action: PayloadAction<apiDetailsObject>) => {
      state.saveData = action.payload;
    },
    incrementTableRefresh: (state) => {
      state.tableRefresh += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApiDetails.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        fetchApiDetails.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.status = "succeeded";
          state.data = action.payload.data;
          state.totalListCount = Number.isNaN(
            Number(action.payload?.data[0]?.total_count),
          )
            ? 0
            : Number(action.payload?.data[0]?.total_count);
        },
      )
      .addCase(fetchApiDetails.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      });
  },
});

// Selectors and Exports
export const selectApiDetails = (state: RootState) => state.apiDetails;
export const { editApiData, saveApiData, incrementTableRefresh } =
  apiDetailsSlice.actions;
export default apiDetailsSlice.reducer;
